import { FormEvent, useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { clientsApi } from "@/api/endpoints";
import type { CustomerProfile } from "@/api/types";
import { useAuth } from "@/auth/AuthContext";
import { canAccess } from "@/auth/permissions";
import { NewClientModal } from "@/components/creates/NewClientModal";
import { PageHeader } from "@/components/PageHeader";
import { SavedViewsBar } from "@/components/SavedViewsBar";
import { CUSTOMER_STATUSES } from "@/constants/crm";
import { customerName, statusClass } from "@/utils/format";

const inputStyle = {
  padding: "0.55rem 0.7rem",
  borderRadius: 10,
  border: "1px solid var(--border)",
  background: "var(--surface, #fff)",
};

export function ClientsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState<CustomerProfile[]>([]);
  const [total, setTotal] = useState(0);
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [sort, setSort] = useState<string | null>(null);
  const [hasBalance, setHasBalance] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    clientsApi
      .query({
        q: search || undefined,
        status: status || undefined,
        sort: sort || undefined,
        hasBalance: hasBalance ? true : undefined,
        limit: 100,
      })
      .then((data) => {
        setItems(data.items);
        setTotal(data.total);
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load clients"))
      .finally(() => setLoading(false));
  }, [search, status, sort, hasBalance]);

  useEffect(() => {
    load();
  }, [load]);

  function onSearch(event: FormEvent) {
    event.preventDefault();
    setSearch(query.trim());
  }

  const topByPremium = sort === "total_premium:desc";
  const currentFilters: Record<string, string> = {};
  if (status) currentFilters.status = status;
  if (search) currentFilters.q = search;
  if (hasBalance) currentFilters.has_balance = "true";

  return (
    <>
      <PageHeader
        title="Clients"
        subtitle="Search, filter, and open Client 360 records."
        actions={
          canAccess(user, "crm.edit") ? (
            <button type="button" className="btn btn-primary" onClick={() => setShowCreate(true)}>
              + New client
            </button>
          ) : null
        }
      />
      <NewClientModal open={showCreate} onClose={() => setShowCreate(false)} />

      <form onSubmit={onSearch} style={{ display: "flex", gap: "0.6rem", alignItems: "center", flexWrap: "wrap" }}>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name, email, or company"
          style={{ ...inputStyle, flex: 1, minWidth: 220 }}
        />
        <button type="submit" className="btn btn-primary">
          Search
        </button>
        <select style={inputStyle} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {CUSTOMER_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <button
          type="button"
          className={`btn ${topByPremium ? "btn-primary" : "btn-secondary"}`}
          onClick={() => setSort(topByPremium ? null : "total_premium:desc")}
        >
          Top by premium
        </button>
        <button
          type="button"
          className={`btn ${hasBalance ? "btn-primary" : "btn-secondary"}`}
          onClick={() => setHasBalance((v) => !v)}
        >
          Has balance
        </button>
      </form>

      <div style={{ margin: "0.6rem 0" }}>
        <SavedViewsBar
          entity="clients"
          filters={currentFilters}
          sort={sort}
          onApply={(f, s) => {
            setStatus(f.status ?? "");
            setSearch(f.q ?? "");
            setQuery(f.q ?? "");
            setHasBalance(f.has_balance === "true");
            setSort(s);
          }}
        />
      </div>

      {error ? <div className="error-banner">{error}</div> : null}
      <div className="card">
        <div style={{ padding: "0.5rem 0.25rem", color: "var(--muted)", fontSize: "0.85rem" }}>{total} clients</div>
        {loading ? (
          <div className="empty-state">Loading clients…</div>
        ) : items.length === 0 ? (
          <div className="empty-state">No clients found.</div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Email</th>
                <th>Status</th>
                <th>Source</th>
              </tr>
            </thead>
            <tbody>
              {items.map((client) => (
                <tr key={client.id}>
                  <td>
                    <Link to={`/clients/${client.id}`}>
                      {customerName(client.first_name, client.last_name, client.company_name)}
                    </Link>
                  </td>
                  <td>{client.email ?? "—"}</td>
                  <td>
                    <span className={statusClass(client.customer_status)}>{client.customer_status}</span>
                  </td>
                  <td>{client.lead_source ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
